import {
  OrderPdfStatus,
  OrderStatus
} from './order-status.model';
// TICKET: sin ticketIds → fan-out tickets libres del padre.
// Con ticketIds → solo esos. Agrupación OS: idItinerary + providerId + tipoMoneda
// (tickets de distintos padres del mismo file pueden ir en 1 OS / N líneas).

export interface CreateOrderItemInput {
  serviceId: string;
  /** Solo TICKET. Omitir = fan-out de todos los tickets libres del padre. */
  ticketIds?: string[] | null;
}

export interface CreateOrderCreatedByInput {
  name: string;
  email: string;
}

export type OrderCommissionType = 'PERCENTAGE' | 'FIXED';

export interface CreateOrderCommissionInput {
  type: OrderCommissionType;
  /** Porcentaje (4 = 4%) o monto fijo. */
  value: number;
}

export interface CreateOrderInput {
  items: CreateOrderItemInput[];
  createdBy: CreateOrderCreatedByInput;
  scheduledPaymentDate?: string | null;
  /** Plazo para confirmar. Vacío: 45 días antes de la fecha del servicio. */
  freezeDeadlineAt?: string | null;
  /** true: nace CONFIRMADO, sin preliminar. */
  asOfficial?: boolean | null;
  reservationNumber?: string | null;
  orderObservation?: string | null;
  /** Ausente: la OS se crea sin comisión. */
  commission?: CreateOrderCommissionInput | null;
}

export interface ConfirmPreliminaryOrderResult {
  orderId: string;
  orderNumber: string;
  orderStatus: string;
  displayCode: string;
  pdfStatus: string;
}

export interface PreliminaryOrdersBatchResult {
  updatedCount: number;
  orderIds: string[];
}

export interface CreatedOrder {
  orderId: string;
  orderNumber: string;
  displayCode?: string | null;
  serviceIds: string[];
  // Presente solo si la OS incluyó tickets.
  ticketIds?: string[] | null;
  idItinerary: string;
  providerId: string;
  currency: string;
  // PRELIMINAR al crear, salvo que el usuario pida nacer confirmada.
  orderStatus: OrderStatus | string;
  scheduledPaymentDate?: string | null;
  freezeDeadlineAt?: string | null;
  reservationNumber?: string | null;
  orderObservation?: string | null;
  // PENDING al crear (el PDF se genera de forma asíncrona).
  pdfStatus: OrderPdfStatus | string;
  baseAmount?: number | null;
  commissionPercent?: number | null;
  commissionAmount?: number | null;
  /** Total a pagar. Incluye la comisión. */
  total?: number | null;
}

export type RejectedServiceReason =
  | 'SERVICE_NOT_FOUND'
  | 'SERVICE_DELETED'
  | 'SERVICE_ALREADY_HAS_ORDER'
  | 'DUPLICATED_INPUT'
  | 'MISSING_AMOUNT'
  | 'INVALID_TICKET'
  | 'NO_AVAILABLE_TICKET'
  | 'TICKET_NOT_FOUND'
  | 'TICKET_INVARIANT_VIOLATION'
  | string;

export interface RejectedService {
  serviceId: string;
  /** Presente en rechazos por ticket (Ticketería). */
  ticketId?: string | null;
  reason: RejectedServiceReason;
  message: string;
  userMessage?: string | null;
}

export interface OrderWarning {
  serviceId?: string | null;
  groupKey?: string | null;
  reason: string;
  message: string;
  userMessage?: string | null;
}

/** @deprecated Prefer CreateOrderJobResult; se mantiene como vista del job COMPLETED. */
export interface CreateOrderResult {
  createdOrders: CreatedOrder[];
  rejectedServices: RejectedService[];
  warnings: OrderWarning[];
}

export enum CreateOrderJobStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED'
}

/** Retorno de createOrder / getCreateOrderJob (async en AWS). */
export interface CreateOrderJobResult {
  jobId: string;
  status: CreateOrderJobStatus | string;
  errorCode?: string | null;
  lastError?: string | null;
  userMessage?: string | null;
  createdOrders?: CreatedOrder[] | null;
  rejectedServices?: RejectedService[] | null;
  warnings?: OrderWarning[] | null;
}

export const CREATE_ORDER_JOB_POLL_INTERVAL_MS = 2500;
export const CREATE_ORDER_JOB_TIMEOUT_MS = 180_000;

export function isCreateOrderJobInProgress(
  status: string | null | undefined
): boolean {
  const normalized = status?.trim().toUpperCase();
  return (
    normalized === CreateOrderJobStatus.PENDING ||
    normalized === CreateOrderJobStatus.PROCESSING
  );
}

export function toCreateOrderResult(
  job: CreateOrderJobResult | null | undefined
): CreateOrderResult {
  return {
    createdOrders: job?.createdOrders ?? [],
    rejectedServices: job?.rejectedServices ?? [],
    warnings: job?.warnings ?? []
  };
}
